
<template>
  <div class="pdf-viewer">
    <div v-if="!pdfUrl" class="no-pdf">
      <p>请在URL中添加url参数来查看PDF文件</p>
      <p>示例: /onlinePreview?url=aHR0cHM6Ly9leGFtcGxlLmNvbS9maWxlLnBkZg==</p>
    </div>
    <div v-else>
      <div v-if="loading" class="loading">
        <div class="loading-text">{{ loadingText }}</div>
        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="progress-text">{{ Math.round(loadingProgress) }}%</div>
        </div>
      </div>
      <div v-if="error" class="error">{{ error }}</div>
      <div class="pdf-container" ref="pdfContainer" v-show="!loading && !error"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PDFViewer',
  data() {
    return {
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      scale: 1.5,
      pdfUrl: this.getPdfUrl(),
      loading: true,
      error: null,
      loadingProgress: 0,
      loadingText: '正在加载PDF...',
      pdfjsLib: null
    };
  },
  mounted() {
    this.initializePDFJS();
    // 监听URL变化（如果需要支持单页应用内的URL变化）
    window.addEventListener('popstate', this.handleUrlChange);
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.handleUrlChange);
  },
  methods: {
    initializePDFJS() {
      // 等待PDF.js库加载完成
      let retryCount = 0;
      const maxRetries = 50; // 最多等待5秒
      
      const checkPDFJS = () => {
        if (window.pdfjsLib) {
          try {
            this.pdfjsLib = window.pdfjsLib;
            // 设置 PDF.js worker - 多个备用方案
            const workerUrls = [
              'https://dl.zhaogang.com/dev/tools/pdf.worker.min.js',
              'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js',
              'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js',
              'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js'
            ];
            
            // 在代理环境下，直接使用fake worker模式
            const isProxyEnvironment = this.detectProxyEnvironment();
            
            if (isProxyEnvironment) {
              console.log('检测到代理环境，使用fake worker模式');
              this.pdfjsLib.GlobalWorkerOptions.workerSrc = false;
            } else {
              // 尝试设置worker，如果失败则使用下一个备用源
              let workerSet = false;
              for (const workerUrl of workerUrls) {
                try {
                  this.pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
                  console.log('Worker设置成功:', workerUrl);
                  workerSet = true;
                  break;
                } catch (error) {
                  console.warn('Worker设置失败:', workerUrl, error);
                  continue;
                }
              }
              
              if (!workerSet) {
                // 如果所有CDN都失败，使用fake worker
                console.log('所有CDN都失败，使用fake worker模式');
                this.pdfjsLib.GlobalWorkerOptions.workerSrc = false;
              }
            }
            console.log('PDF.js 初始化成功');
            this.loadPDF();
          } catch (error) {
            console.error('PDF.js 初始化失败:', error);
            this.error = 'PDF.js 库初始化失败，请刷新页面重试';
            this.loading = false;
          }
        } else {
          retryCount++;
          if (retryCount >= maxRetries) {
            console.error('PDF.js 库加载超时');
            this.error = 'PDF.js 库加载超时，请检查网络连接或刷新页面重试';
            this.loading = false;
            return;
          }
          // 如果还没加载完成，等待100ms后重试
          setTimeout(checkPDFJS, 100);
        }
      };
      
      checkPDFJS();
    },
    detectProxyEnvironment() {
      // 检测是否在代理环境下
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobile = /mobile|android|iphone|ipad|phone/i.test(userAgent);
      
      // 检查网络连接类型
      if ('connection' in navigator) {
        const connection = navigator.connection;
        if (connection && connection.effectiveType) {
          console.log('网络类型:', connection.effectiveType);
        }
      }
      
      // 检查是否在移动设备上
      if (isMobile) {
        console.log('检测到移动设备，可能使用代理');
        return true;
      }
      
      // 检查是否在特定环境下（如企业网络）
      const hostname = window.location.hostname;
      if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        return false; // 本地开发环境
      }
      
      // 默认在移动设备上使用fake worker
      return isMobile;
    },
    async validateWorker() {
      // 在代理环境下，我们已经使用了fake worker，所以不需要额外验证
      console.log('Worker配置完成，当前模式:', this.pdfjsLib.GlobalWorkerOptions.workerSrc === false ? 'fake worker' : 'web worker');
    },
    getPdfUrl() {
       // 获取URL参数
       const urlParams = new URLSearchParams(window.location.search);
       const pdfB64Url = urlParams.get('url');
       
       if (pdfB64Url) {
         try {
           // 解码Base64
           const decodedUrl = atob(pdfB64Url);
           console.log('解码后的PDF URL:', decodedUrl);
           return decodedUrl;
         } catch (error) {
           console.error('Base64解码失败:', error);
           return null;
         }
       }
       
       // 如果没有URL参数，不显示PDF
       return null;
      },
     getPdfDisplayName() {
        const urlParams = new URLSearchParams(window.location.search);
        const pdfB64Url = urlParams.get('url');
        
        if (pdfB64Url) {
          try {
            const decodedUrl = atob(pdfB64Url);
            // 提取文件名或显示简化的URL
            const fileName = decodedUrl.split('/').pop() || decodedUrl;
            return `远程文件 (${fileName})`;
          } catch (error) {
            return '解码失败';
          }
        }
        
        return '请通过URL参数提供PDF文件';
      },
    async loadPDF() {
        // 如果没有PDF URL，不加载
        if (!this.pdfUrl) {
          this.loading = false;
          return;
        }
        
        try {
          this.loading = true;
          this.error = null;
          this.loadingProgress = 0;
          this.loadingText = '正在加载PDF...';
          
          // 检查PDF.js是否已加载
          if (!this.pdfjsLib) {
            throw new Error('PDF.js库未加载');
          }
          
          // 验证worker是否可用
          await this.validateWorker();
          
          console.log('开始加载PDF:', this.pdfUrl);
          
          // 创建加载任务并监听进度
          const loadingTask = this.pdfjsLib.getDocument({
            url: this.pdfUrl,
            onProgress: (progress) => {
              if (progress.total > 0) {
                const percent = (progress.loaded / progress.total) * 50; // PDF加载占50%
                this.loadingProgress = Math.min(percent, 50);
                this.loadingText = `正在下载PDF... (${Math.round(progress.loaded / 1024)}KB / ${Math.round(progress.total / 1024)}KB)`;
              }
            }
          });
          
          this.pdfDoc = await loadingTask.promise;
          this.totalPages = this.pdfDoc.numPages;
          console.log('PDF加载成功，总页数:', this.totalPages);
          
          this.loadingProgress = 50;
          this.loadingText = '正在渲染页面...';
          
          await this.renderAllPages();
          this.loading = false;
        } catch (error) {
          console.error('加载PDF失败:', error);
          this.error = `加载PDF失败: ${error.message}`;
          this.loading = false;
        }
      },
      async renderAllPages() {
        if (!this.pdfDoc) return;
        
        const container = this.$refs.pdfContainer;
        if (!container) {
          throw new Error('PDF容器未找到');
        }
        
        // 清空容器
        container.innerHTML = '';
        
        // 设置容器宽度 - 满屏显示
        const containerWidth = window.innerWidth;
        
        try {
          for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
            console.log('渲染页面:', pageNum);
            
            // 更新渲染进度 (50% + 页面进度的50%)
            const pageProgress = ((pageNum - 1) / this.totalPages) * 50;
            this.loadingProgress = 50 + pageProgress;
            this.loadingText = `正在渲染页面 ${pageNum}/${this.totalPages}...`;
            
            const page = await this.pdfDoc.getPage(pageNum);
            
            // 为每一页创建canvas
            const canvas = document.createElement('canvas');
            canvas.className = 'pdf-page';
            const context = canvas.getContext('2d');
            
            // 获取设备像素比，用于高DPI设备
            const devicePixelRatio = window.devicePixelRatio || 1;
            
            // 计算适合全屏宽度的缩放比例
            const originalViewport = page.getViewport({ scale: 1.0 });
            const baseScale = containerWidth / originalViewport.width;
            // 为了更高清晰度，增加额外的缩放倍数
            const highQualityScale = baseScale * Math.max(devicePixelRatio, 2);
            const viewport = page.getViewport({ scale: highQualityScale });
            
            // 设置canvas的实际尺寸（高分辨率）
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            
            // 设置canvas的显示尺寸（全屏宽度）
            canvas.style.width = '100%';
            canvas.style.height = 'auto';
            canvas.style.maxWidth = '100%';
            
            // 启用图像平滑以获得更好的渲染质量
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            
            const renderContext = {
              canvasContext: context,
              viewport: viewport
            };
            
            await page.render(renderContext).promise;
            container.appendChild(canvas);
            
            console.log('页面渲染完成:', pageNum);
          }
          
          // 渲染完成
          this.loadingProgress = 100;
          this.loadingText = '渲染完成';
        } catch (error) {
          console.error('渲染页面失败:', error);
          throw error;
        }
      },
    handleUrlChange() {
      // URL变化时重新获取PDF URL并加载
      const newPdfUrl = this.getPdfUrl();
      if (newPdfUrl !== this.pdfUrl) {
        this.pdfUrl = newPdfUrl;
        this.currentPage = 1;
        if (this.pdfjsLib) {
          this.loadPDF();
        } else {
          this.initializePDFJS();
        }
      }
    }
  }
};
</script>

<style>
/* 全局样式 - 去掉所有默认边距 */
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

#app {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
}
</style>

<style scoped>
.pdf-viewer {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  margin: 0;
  width: 100vw;
  height: 100vh;
  overflow: auto;
}

.pdf-info {
  margin-bottom: 15px;
  padding: 10px 15px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  color: #495057;
}

.pdf-url-info {
  font-weight: 500;
}

.no-pdf {
  text-align: center;
  padding: 40px;
  color: #666;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.no-pdf p {
  margin: 10px 0;
  font-size: 16px;
}

.loading {
  text-align: center;
  padding: 0;
  color: #666;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding-top: 38.2vh;
}

.loading-text {
  font-size: 16px;
  margin-bottom: 20px;
  color: #333;
}

.progress-container {
  max-width: 600px;
  width: 80%;
  margin: 0 auto;
}

.progress-bar {
  width: 100%;
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 10px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.2);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #45a049);
  border-radius: 10px;
  transition: width 0.3s ease;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.progress-text {
  font-size: 14px;
  font-weight: 500;
  color: #555;
}

.pdf-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  padding: 0;
  margin: 0;
  background-color: #fafafa;
  min-height: 100vh;
}

.pdf-page {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  background: #ffffff;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100%;
}

.no-pdf p:first-child {
  font-weight: 500;
  color: #495057;
}

.no-pdf p:last-child {
  font-family: monospace;
  background-color: #e9ecef;
  padding: 10px;
  border-radius: 4px;
  font-size: 14px;
}

.error {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #e74c3c;
  background-color: #fdf2f2;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  margin: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>